import { FloatingLabel } from "@/components/FloatingLabel";
import CitySelect from "@/components/hlidaci-pes/CitySelect";
import { MultiSelect } from "@/components/modals/MultiSelect";
import { Button } from "@/components/ui/Button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { FormDataWatchDog } from "@/types";
import {
  comercialOptions,
  estateConditionOptions,
  flatOptions,
  homeOptions,
  landOptions,
  otherOptions,
} from "@/utils/EstateForm";
import { getEstateValueLabel } from "@/utils/estateValueLabels";
import { Check, X } from "lucide-react";
import React, { useEffect } from "react";
import { Switch } from "../ui/Switch";

type CreateNewWatchDogProps = {
  embedded?: boolean;
};

export function CreateNewWatchDog({
  embedded = false,
}: CreateNewWatchDogProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      createNew: "Vytvořit nového",
      createTitle: "Vytvořit nového hlídacího psa",
      editTitle: "Upravit hlídacího psa",
      description: "Vyberte pole ze seznamu",
      settings: "Nastavení hlídacího psa",
      offerType: "Typ nabídky",
      sale: "Prodej",
      rent: "Pronájem",
      estateType: "Typ nemovitosti",
      flats: "Byty",
      houses: "Domy",
      land: "Pozemky",
      commercial: "Komerční",
      other: "Ostatní",
      notSet: "Nezadáno",
      city: "Město",
      searchAround: "Hledat v okolí",
      showDetailedSearch: "Zobrazit podrobnější hledání",
      detailedSearchSettings: "Nastavení podrobného hledání",
      disposition: "Dispozice",
      category: "Kategorie",
      landCategory: "Kategorie pozemků",
      commercialCategory: "Kategorie komerčních objektů",
      otherCategory: "Ostatní kategorie",
      condition: "Stav",
      floorFrom: "Podlaží od",
      floorTo: "Podlaží do",
      usableAreaFrom: "Užitná plocha od",
      usableAreaTo: "Užitná plocha do",
      priceFrom: "Cena od",
      priceTo: "Cena do",
      notificationSettings: "Nastavení upozornění",
      sendOffersEmail: "Zasílat nabídky na e-mail",
      delivery: "Doručení",
      sendingFrequency: "Frekvence zasílání",
      sendingSettings: "Nastavení zasílání",
      listingSettings: "Nastavení inzerátů",
      nonstop: "Bezprostředně",
      often: "Shrnutí každé 2 hodiny",
      daily: "Shrnutí za poslední den",
      onlyNew: "Jen nové inzeráty",
      onlyUpdated: "Jen aktualizované inzeráty",
      newAndUpdated: "Nové i aktualizované inzeráty",
      eachOffer: "Každou nabídku zvlášť",
      thisWatchdog: "Tento hlídací pes zvlášť",
      allTogether: "Vše dohromady v 1 e-mailu",
      discard: "Zahodit",
      continue: "Pokračovat",
      clearAndBack: "Vymazat a jít zpět",
      back: "Krok zpět",
      save: "Uložit",
    },
    en: {
      createNew: "Create new",
      createTitle: "Create a new watchdog",
      editTitle: "Edit watchdog",
      description: "Select fields from the list",
      settings: "Watchdog settings",
      offerType: "Offer type",
      sale: "Sale",
      rent: "Rent",
      estateType: "Property type",
      flats: "Flats",
      houses: "Houses",
      land: "Land",
      commercial: "Commercial",
      other: "Other",
      notSet: "Not set",
      city: "City",
      searchAround: "Search nearby",
      showDetailedSearch: "Show detailed search",
      detailedSearchSettings: "Detailed search settings",
      disposition: "Disposition",
      category: "Category",
      landCategory: "Land category",
      commercialCategory: "Commercial property category",
      otherCategory: "Other category",
      condition: "Condition",
      floorFrom: "Floor from",
      floorTo: "Floor to",
      usableAreaFrom: "Usable area from",
      usableAreaTo: "Usable area to",
      priceFrom: "Price from",
      priceTo: "Price to",
      notificationSettings: "Notification settings",
      sendOffersEmail: "Send offers by email",
      delivery: "Delivery",
      sendingFrequency: "Sending frequency",
      sendingSettings: "Sending settings",
      listingSettings: "Listing settings",
      nonstop: "Immediately",
      often: "Summary every 2 hours",
      daily: "Summary for the last day",
      onlyNew: "Only new listings",
      onlyUpdated: "Only updated listings",
      newAndUpdated: "New and updated listings",
      eachOffer: "Each offer separately",
      thisWatchdog: "This watchdog separately",
      allTogether: "Everything together in 1 email",
      discard: "Discard",
      continue: "Continue",
      clearAndBack: "Clear and go back",
      back: "Back",
      save: "Save",
    },
  }[language];
  const { isLoading, userInfoGlobal } = useAppContext();
  const {
    formDataWatchDog,
    setFormDataWatchDog,
    handleDiscardChanges,
    saveWatchDog,
    stepNumber,
    setStepNumber,
    resetDetailSearch,
    selectedItem,
  } = useMenuItemsContext();

  const isFormValid =
    formDataWatchDog.estateType &&
    formDataWatchDog.offerType &&
    formDataWatchDog.exactAddress?.trim() &&
    formDataWatchDog.inRange;

  const isNew =
    embedded ||
    selectedItem?.name === "Vytvořit nového" ||
    selectedItem?.name === copy.createNew;
  const offerTypeOptions = [
    { value: "Prodej", label: copy.sale },
    { value: "Pronájem", label: copy.rent },
  ] as const;
  const selectedOfferTypeLabel =
    offerTypeOptions.find(({ value }) => value === formDataWatchDog.offerType)
      ?.label ?? "";
  const estateTypeOptions = [
    { value: "Byty", label: copy.flats },
    { value: "Domy", label: copy.houses },
    { value: "Pozemky", label: copy.land },
    { value: "Komerční", label: copy.commercial },
    { value: "Ostatní", label: copy.other },
  ] as const;
  const selectedEstateTypeLabel =
    estateTypeOptions.find(({ value }) => value === formDataWatchDog.estateType)
      ?.label ?? "";
  const inRangeOptions = [
    { value: "Nezadáno", label: copy.notSet },
    { value: "0,5 km", label: "0,5 km" },
    { value: "1 km", label: "1 km" },
    { value: "5 km", label: "5 km" },
    { value: "10 km", label: "10 km" },
    { value: "25 km", label: "25 km" },
  ] as const;
  const selectedInRangeLabel =
    inRangeOptions.find(({ value }) => value === formDataWatchDog.inRange)
      ?.label ?? "";
  const emailReportFrequencyOptions = [
    {
      value: "NONSTOP",
      label: copy.nonstop,
      disabled:
        userInfoGlobal?.membership !== "PROFESSIONAL" &&
        userInfoGlobal?.membership !== "BASIC",
    },
    {
      value: "OFTEN",
      label: copy.often,
      disabled:
        userInfoGlobal?.membership !== "PREMIUM" &&
        userInfoGlobal?.membership !== "PROFESSIONAL" &&
        userInfoGlobal?.membership !== "BASIC",
    },
    {
      value: "DAILY",
      label: copy.daily,
      disabled: false,
    },
  ] as const;
  const selectedEmailReportFrequencyLabel =
    emailReportFrequencyOptions.find(
      ({ value }) => value === formDataWatchDog.emailReportFrequency,
    )?.label ?? "";
  const sendOfferAsOptions = [
    { value: "NEW", label: copy.onlyNew },
    { value: "UPDATED", label: copy.onlyUpdated },
    { value: "ALL", label: copy.newAndUpdated },
  ] as const;
  const selectedSendOfferAsLabel =
    sendOfferAsOptions.find(
      ({ value }) => value === formDataWatchDog.sendOfferAs,
    )?.label ?? "";
  const emailReportSettingOptions = [
    {
      value: "SINGLE",
      label: copy.eachOffer,
      disabled:
        userInfoGlobal?.membership !== "PROFESSIONAL" &&
        userInfoGlobal?.membership !== "BASIC",
    },
    {
      value: "GROUP_BY_WATCHDOG",
      label: copy.thisWatchdog,
      disabled: false,
    },
    {
      value: "GROUP_BY_ALL",
      label: copy.allTogether,
      disabled: false,
    },
  ] as const;
  const selectedEmailReportSettingLabel =
    emailReportSettingOptions.find(
      ({ value }) => value === formDataWatchDog.emailReportSetting,
    )?.label ?? "";
  const getLocalizedOptions = (options: string[]) =>
    options.map((value) => getEstateValueLabel(value, language) ?? value);
  const getSelectedLabels = (values: string[]) =>
    values.map((value) => getEstateValueLabel(value, language) ?? value);
  const getStoredValuesFromLabels = (labels: string[], options: string[]) =>
    labels.map(
      (label) =>
        options.find(
          (option) => getEstateValueLabel(option, language) === label,
        ) ?? label,
    );
  const defaultEmailReportFrequency =
    userInfoGlobal?.membership === "STANDARD"
      ? "DAILY"
      : userInfoGlobal?.membership === "PREMIUM" ||
          userInfoGlobal?.membership === "BASIC"
        ? "OFTEN"
        : "NONSTOP";

  useEffect(() => {
    const isKnownFrequency = emailReportFrequencyOptions.some(
      ({ value }) => value === formDataWatchDog.emailReportFrequency,
    );

    if (!isKnownFrequency) {
      setFormDataWatchDog((prev) => ({
        ...prev,
        emailReportFrequency: defaultEmailReportFrequency,
      }));
    }
  }, [
    defaultEmailReportFrequency,
    emailReportFrequencyOptions,
    formDataWatchDog.emailReportFrequency,
    setFormDataWatchDog,
  ]);

  const form = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const updatedWatchDog: FormDataWatchDog = {
          ...formDataWatchDog,
          isNew: isNew,
        };
        setFormDataWatchDog(updatedWatchDog);
        saveWatchDog(updatedWatchDog);
      }}
      className={embedded ? "space-y-2" : "space-y-4"}
    >
      {embedded ? (
        <header className="text-left">
          <h3 className="app-card-title text-base leading-tight">
            {copy.createTitle}
          </h3>
          <p className="app-section-description mt-1">{copy.description}</p>
        </header>
      ) : (
        <DialogHeader>
          <DialogTitle>{isNew ? copy.createTitle : copy.editTitle}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
      )}

      {stepNumber === 1 ? (
        <div
          className={
            embedded
              ? "grid items-center justify-items-center gap-2 py-2"
              : "grid items-center justify-items-center gap-3 py-4"
          }
        >
          {embedded ? (
            <p className="text-sm text-foreground">{copy.settings}</p>
          ) : (
            <DialogDescription>{copy.settings}</DialogDescription>
          )}
          <div
            data-tour="watchdog-offer-type-field"
            className={`app-form-row ${embedded ? "w-full" : "w-[75%]"}`}
          >
            <FloatingLabel
              htmlFor="offerType"
              value={formDataWatchDog.offerType}
            >
              {copy.offerType}
            </FloatingLabel>
            <MultiSelect
              disabled={!isNew}
              options={offerTypeOptions.map(({ label }) => label)}
              selected={selectedOfferTypeLabel ? [selectedOfferTypeLabel] : []}
              setSelected={(values) => {
                const selectedLabel = values.at(-1);
                const selectedOption = offerTypeOptions.find(
                  ({ label }) => label === selectedLabel,
                );

                if (!selectedOption) {
                  return;
                }

                setFormDataWatchDog({
                  ...formDataWatchDog,
                  doResetSearch: !isNew,
                  offerType: selectedOption.value,
                });
              }}
              selectionMode="single"
              showSearch={false}
              placeholder={copy.offerType}
              className="col-span-4 mb-0 w-full"
            />
          </div>
          <div
            data-tour="watchdog-estate-type-field"
            className={`app-form-row ${embedded ? "w-full" : "w-[75%]"}`}
          >
            <FloatingLabel
              htmlFor="estateType"
              value={formDataWatchDog.estateType}
            >
              {copy.estateType}
            </FloatingLabel>
            <MultiSelect
              options={estateTypeOptions.map(({ label }) => label)}
              selected={
                selectedEstateTypeLabel ? [selectedEstateTypeLabel] : []
              }
              setSelected={(values) => {
                const selectedLabel = values.at(-1);
                const selectedOption = estateTypeOptions.find(
                  ({ label }) => label === selectedLabel,
                );

                if (!selectedOption) {
                  return;
                }

                setFormDataWatchDog({
                  ...formDataWatchDog,
                  doResetSearch: !isNew,
                  estateType: selectedOption.value,
                });
              }}
              selectionMode="single"
              showSearch={false}
              placeholder={copy.estateType}
              className="col-span-4 mb-0 w-full"
            />
          </div>
          <div
            data-tour="watchdog-city-field"
            className={`app-form-row ${embedded ? "w-full" : "w-[75%]"}`}
          >
            <FloatingLabel
              htmlFor="exactAddress"
              value={formDataWatchDog.exactAddress}
            >
              {copy.city}
            </FloatingLabel>
            <CitySelect
              isNew={isNew}
              formData={formDataWatchDog}
              setFormData={setFormDataWatchDog}
            />
          </div>
          <div
            data-tour="watchdog-radius-field"
            className={`app-form-row ${embedded ? "w-full" : "w-[75%]"}`}
          >
            <Label htmlFor="inRange" className="text-right">
              {copy.searchAround}
            </Label>
            <MultiSelect
              options={inRangeOptions.map(({ label }) => label)}
              selected={selectedInRangeLabel ? [selectedInRangeLabel] : []}
              setSelected={(values) => {
                const selectedLabel = values.at(-1);
                const selectedOption = inRangeOptions.find(
                  ({ label }) => label === selectedLabel,
                );

                if (!selectedOption) {
                  return;
                }

                setFormDataWatchDog({
                  ...formDataWatchDog,
                  doResetSearch: !isNew,
                  inRange: selectedOption.value,
                });
              }}
              selectionMode="single"
              showSearch={false}
              placeholder={copy.searchAround}
              className="col-span-3 mb-0 w-full"
            />
          </div>
          <Button
            data-tour="watchdog-detailed-search-button"
            type="button"
            variant="secondary"
            onClick={() => setStepNumber(2)}
            className={embedded ? "ml-0" : "ml-2"}
          >
            {copy.showDetailedSearch}
          </Button>
        </div>
      ) : stepNumber === 2 ? (
        <div
          className={
            embedded
              ? "grid items-center justify-items-center gap-2 py-2"
              : "grid items-center justify-items-center gap-3 py-4"
          }
        >
          {embedded ? (
            <p className="text-sm text-foreground">
              {copy.detailedSearchSettings}
            </p>
          ) : (
            <DialogDescription>{copy.detailedSearchSettings}</DialogDescription>
          )}
          <div className="app-form-row app-form-row--narrow">
            <div className="flex items-center space-x-2 col-span-4">
              {formDataWatchDog.estateType === "Byty" ? (
                <MultiSelect
                  options={getLocalizedOptions(flatOptions)}
                  selected={getSelectedLabels(formDataWatchDog.flatCategory)}
                  setSelected={(values) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doResetSearch: !isNew,
                      flatCategory: getStoredValuesFromLabels(
                        values,
                        flatOptions,
                      ),
                    })
                  }
                  placeholder={copy.disposition}
                />
              ) : formDataWatchDog.estateType === "Domy" ? (
                <MultiSelect
                  options={getLocalizedOptions(homeOptions)}
                  selected={getSelectedLabels(formDataWatchDog.homeCategory)}
                  setSelected={(values) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doResetSearch: !isNew,
                      homeCategory: getStoredValuesFromLabels(
                        values,
                        homeOptions,
                      ),
                    })
                  }
                  placeholder={copy.category}
                />
              ) : formDataWatchDog.estateType === "Pozemky" ? (
                <MultiSelect
                  options={getLocalizedOptions(landOptions)}
                  selected={getSelectedLabels(formDataWatchDog.landCategory)}
                  setSelected={(values) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doResetSearch: !isNew,
                      landCategory: getStoredValuesFromLabels(
                        values,
                        landOptions,
                      ),
                    })
                  }
                  placeholder={copy.landCategory}
                />
              ) : formDataWatchDog.estateType === "Komerční" ? (
                <MultiSelect
                  options={getLocalizedOptions(comercialOptions)}
                  selected={getSelectedLabels(
                    formDataWatchDog.comercialCategory,
                  )}
                  setSelected={(values) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doResetSearch: !isNew,
                      comercialCategory: getStoredValuesFromLabels(
                        values,
                        comercialOptions,
                      ),
                    })
                  }
                  placeholder={copy.commercialCategory}
                />
              ) : formDataWatchDog.estateType === "Ostatní" ? (
                <MultiSelect
                  options={getLocalizedOptions(otherOptions)}
                  selected={getSelectedLabels(formDataWatchDog.otherCategory)}
                  setSelected={(values) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doResetSearch: !isNew,
                      otherCategory: getStoredValuesFromLabels(
                        values,
                        otherOptions,
                      ),
                    })
                  }
                  placeholder={copy.otherCategory}
                />
              ) : null}
            </div>
          </div>

          <div className="app-form-row app-form-row--narrow">
            <MultiSelect
              options={getLocalizedOptions(estateConditionOptions)}
              selected={getSelectedLabels(formDataWatchDog.estateCondition)}
              setSelected={(values) =>
                setFormDataWatchDog({
                  ...formDataWatchDog,
                  doResetSearch: !isNew,
                  estateCondition: getStoredValuesFromLabels(
                    values,
                    estateConditionOptions,
                  ),
                })
              }
              placeholder={copy.condition}
            />
          </div>

          <div className="app-form-row app-form-row--narrow">
            <div className="flex items-center gap-3 w-full col-span-4">
              <Input
                type="number"
                placeholder={copy.floorFrom}
                value={formDataWatchDog.floorFrom ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    floorFrom:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={-1}
              />
              <span className="text-sm text-foreground">-</span>
              <Input
                type="number"
                placeholder={copy.floorTo}
                value={formDataWatchDog.floorTo ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    floorTo:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={formDataWatchDog.floorFrom ?? 0}
              />
            </div>
          </div>
          <div className="app-form-row app-form-row--narrow">
            <div className="flex items-center gap-3 w-full col-span-4">
              <Input
                type="number"
                placeholder={copy.usableAreaFrom}
                value={formDataWatchDog.usableAreaFrom ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    usableAreaFrom:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={0}
              />
              <span className="text-sm text-foreground">-</span>
              <Input
                type="number"
                placeholder={copy.usableAreaTo}
                value={formDataWatchDog.usableAreaTo ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    usableAreaTo:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={formDataWatchDog.usableAreaFrom ?? 0}
              />
            </div>
          </div>
          <div className="app-form-row app-form-row--narrow">
            <div className="flex items-center gap-3 w-full col-span-4">
              <Input
                type="number"
                placeholder={copy.priceFrom}
                value={formDataWatchDog.priceFrom ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    priceFrom:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={0}
              />
              <span className="text-sm text-foreground">-</span>
              <Input
                type="number"
                placeholder={copy.priceTo}
                value={formDataWatchDog.priceTo ?? ""}
                onChange={(e) =>
                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    doResetSearch: !isNew,
                    priceTo:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className="w-1/2"
                min={formDataWatchDog.priceFrom ?? 0}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={
            embedded
              ? "grid items-center justify-items-center gap-2 py-2"
              : "grid items-center justify-items-center gap-3 py-4"
          }
        >
          {embedded ? (
            <p className="text-sm text-foreground">
              {copy.notificationSettings}
            </p>
          ) : (
            <DialogDescription>{copy.notificationSettings}</DialogDescription>
          )}
          <div className={embedded ? "w-full" : "grid w-[75%] grid-cols-4"}>
            <div
              className={
                embedded
                  ? "flex w-full items-center justify-between gap-3"
                  : "col-span-4 flex items-center space-x-2"
              }
            >
              <Label htmlFor="doEmailReport" className="leading-tight">
                {copy.sendOffersEmail}
              </Label>
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  id="doEmailReport"
                  checked={formDataWatchDog.doEmailReport}
                  onCheckedChange={(checked) =>
                    setFormDataWatchDog({
                      ...formDataWatchDog,
                      doEmailReport: checked,
                    })
                  }
                  className="data-[state=checked]:bg-softiq-primary"
                />
                {formDataWatchDog.doEmailReport ? (
                  <div className="rounded-full bg-softiq-primary p-2 text-white">
                    <Check height={20} width={20} className="text-white" />
                  </div>
                ) : (
                  <div className="rounded-full bg-softiq-primary p-2 text-white">
                    <X height={20} width={20} className="text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={
              embedded
                ? "app-form-row w-full"
                : "app-form-row app-form-row--narrow"
            }
          >
            <Label
              htmlFor="emailReportFrequency"
              className={embedded ? "text-left" : "text-right"}
            >
              {copy.delivery}
            </Label>
            <MultiSelect
              options={emailReportFrequencyOptions.map(({ label }) => label)}
              selected={
                selectedEmailReportFrequencyLabel
                  ? [selectedEmailReportFrequencyLabel]
                  : []
              }
              setSelected={(values) => {
                const selectedLabel = values.at(-1);
                const selectedOption = emailReportFrequencyOptions.find(
                  ({ label }) => label === selectedLabel,
                );

                if (!selectedOption || selectedOption.disabled) {
                  return;
                }

                setFormDataWatchDog({
                  ...formDataWatchDog,
                  emailReportFrequency: selectedOption.value,
                });
              }}
              disabledOptions={emailReportFrequencyOptions
                .filter(({ disabled }) => disabled)
                .map(({ label }) => label)}
              selectionMode="single"
              showSearch={false}
              placeholder={copy.sendingFrequency}
              className="col-span-3 mb-0 w-full"
              codexId="emailReportFrequency"
            />
          </div>
          {formDataWatchDog.doEmailReport &&
            formDataWatchDog.emailReportFrequency !== "NONSTOP" && (
              <>
                <div
                  className={
                    embedded
                      ? "app-form-row w-full"
                      : "app-form-row app-form-row--narrow"
                  }
                >
                  <Label
                    htmlFor="emailReportSetting"
                    className={embedded ? "text-left" : "text-right"}
                  >
                    {copy.sendingSettings}
                  </Label>
                  <MultiSelect
                    options={emailReportSettingOptions.map(
                      ({ label }) => label,
                    )}
                    selected={
                      selectedEmailReportSettingLabel
                        ? [selectedEmailReportSettingLabel]
                        : []
                    }
                    setSelected={(values) => {
                      const selectedLabel = values.at(-1);
                      const selectedOption = emailReportSettingOptions.find(
                        ({ label }) => label === selectedLabel,
                      );

                      if (!selectedOption || selectedOption.disabled) {
                        return;
                      }

                      setFormDataWatchDog({
                        ...formDataWatchDog,
                        emailReportSetting: selectedOption.value,
                      });
                    }}
                    disabledOptions={emailReportSettingOptions
                      .filter(({ disabled }) => disabled)
                      .map(({ label }) => label)}
                    selectionMode="single"
                    showSearch={false}
                    placeholder={copy.sendingSettings}
                    className="col-span-3 mb-0 w-full"
                  />
                </div>
              </>
            )}
          <div
            className={
              embedded
                ? "app-form-row w-full"
                : "app-form-row app-form-row--narrow"
            }
          >
            <Label
              htmlFor="sendOfferAs"
              className={embedded ? "text-left" : "text-right"}
            >
              {copy.listingSettings}
            </Label>
            <div
              className={
                embedded
                  ? "col-span-3 flex min-w-0 items-center"
                  : "col-span-3 flex items-center space-x-2"
              }
            >
              <MultiSelect
                options={sendOfferAsOptions.map(({ label }) => label)}
                selected={
                  selectedSendOfferAsLabel ? [selectedSendOfferAsLabel] : []
                }
                setSelected={(values) => {
                  const selectedLabel = values.at(-1);
                  const selectedOption = sendOfferAsOptions.find(
                    ({ label }) => label === selectedLabel,
                  );

                  if (!selectedOption) {
                    return;
                  }

                  setFormDataWatchDog({
                    ...formDataWatchDog,
                    sendOfferAs: selectedOption.value,
                  });
                }}
                selectionMode="single"
                showSearch={false}
                placeholder={copy.listingSettings}
                className="mb-0 w-full"
                codexId="sendOfferAs"
              />
            </div>
          </div>
        </div>
      )}

      {embedded ? (
        <div className="flex flex-row flex-wrap items-center justify-end gap-3">
          {renderFormActions()}
        </div>
      ) : (
        <DialogFooter className="flex flex-row justify-end items-center gap-3">
          {renderFormActions()}
        </DialogFooter>
      )}
    </form>
  );

  function renderFormActions() {
    return (
      <>
        {stepNumber === 1 ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDiscardChanges()}
              className="ml-2"
            >
              {copy.discard}
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={!isFormValid}
              className="relative"
              onClick={(e) => {
                e.preventDefault();
                setStepNumber(3);
              }}
            >
              {copy.continue}
            </Button>
          </>
        ) : stepNumber === 2 ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => resetDetailSearch()}
              className="ml-2"
            >
              {copy.clearAndBack}
            </Button>
            <Button
              type="button"
              variant="default"
              className="relative"
              onClick={() => setStepNumber(1)}
            >
              {copy.continue}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStepNumber(1)}
              className="ml-2"
            >
              {copy.back}
            </Button>

            <Button
              type="submit"
              disabled={isLoading || !isFormValid}
              variant="default"
              className="relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{copy.save}</span>
                  <span className="dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </span>
                </>
              ) : (
                copy.save
              )}
            </Button>
          </>
        )}
      </>
    );
  }

  if (embedded) {
    return <div data-tour="watchdog-create-form">{form}</div>;
  }

  return <DialogContent className="sm:max-w-[425px]">{form}</DialogContent>;
}

export function NewWatchdogDialog() {
  return <CreateNewWatchDog embedded />;
}
